import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Form, InputGroup, Pagination, Alert } from 'react-bootstrap';
import { FaClipboardList, FaClock, FaCheckCircle, FaExclamationTriangle, FaSearch, FaEye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Header from '../../components/common/Header';
import AssignmentService from '../../services/assignmentService';
import '../../styles/StudentAssignments.css';

const StudentAssignmentsPage = () => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // Pagination
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1,
  });
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    loadAssignments();
  }, [pagination.current_page, itemsPerPage, statusFilter, searchTerm]);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      const response = await AssignmentService.getMyAssignments(
        pagination.current_page,
        itemsPerPage,
        statusFilter,
        searchTerm
      );
      
      if (response.data) {
        setAssignments(response.data.data || []);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      }
    } catch (error) {
      console.error('Failed to load assignments:', error);
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAssignment = (assignmentId) => {
    navigate(`/student/assignments/${assignmentId}`);
  };

  const getStatusBadge = (status, dueDate) => {
    if (status === 'graded') {
      return <Badge bg="success"><FaCheckCircle className="me-1" /> Graded</Badge>;
    }
    if (status === 'submitted') {
      return <Badge bg="info">Submitted (Pending)</Badge>;
    }
    if (status === 'overdue') {
      return <Badge bg="danger"><FaExclamationTriangle className="me-1" /> Overdue</Badge>;
    }
    return <Badge bg="warning">Pending</Badge>;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date();
  };

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.total_pages) {
      setPagination(prev => ({ ...prev, current_page: pageNumber }));
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleStatusFilterChange = (filter) => {
    setStatusFilter(filter);
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const renderPagination = () => {
    const { current_page, total_pages } = pagination;
    if (total_pages <= 1) return null;

    let items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, current_page - Math.floor(maxVisible / 2));
    let endPage = Math.min(total_pages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(current_page - 1)}
        disabled={current_page === 1}
      />
    );

    if (startPage > 1) {
      items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
      if (startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item key={i} active={i === current_page} onClick={() => handlePageChange(i)}>
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < total_pages) {
      if (endPage < total_pages - 1) items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
      items.push(
        <Pagination.Item key={total_pages} onClick={() => handlePageChange(total_pages)}>
          {total_pages}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(current_page + 1)}
        disabled={current_page === total_pages}
      />
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="d-flex align-items-center gap-2">
          <Form.Select size="sm" value={itemsPerPage} onChange={handleItemsPerPageChange} style={{ width: 'auto' }}>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </Form.Select>
          <span className="text-muted small">
            Showing {(pagination.current_page - 1) * itemsPerPage + 1} -{' '}
            {Math.min(pagination.current_page * itemsPerPage, pagination.total)} of {pagination.total} assignments
          </span>
        </div>
        <Pagination size="sm">{items}</Pagination>
      </div>
    );
  };

  return (
    <div>
      <Header />
      <div className="student-assignments-page">
        <Container className="py-5">
          {/* Page Header */}
          <div className="text-center mb-5">
            <h1 className="display-5 fw-bold">
              <FaClipboardList className="me-3 text-primary" />
              My Assignments
            </h1>
            <p className="text-muted">Track and submit your assignments from all your courses</p>
          </div>

          {/* Filters and Search */}
          <Row className="mb-4 align-items-center">
            <Col md={6}>
              <div className="d-flex gap-2">
                <Button
                  variant={statusFilter === 'all' ? 'primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => handleStatusFilterChange('all')}
                >
                  All
                </Button>
                <Button
                  variant={statusFilter === 'pending' ? 'primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => handleStatusFilterChange('pending')}
                >
                  Pending
                </Button>
                <Button
                  variant={statusFilter === 'submitted' ? 'primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => handleStatusFilterChange('submitted')}
                >
                  Submitted
                </Button>
                <Button
                  variant={statusFilter === 'graded' ? 'primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => handleStatusFilterChange('graded')}
                >
                  Graded
                </Button>
                <Button
                  variant={statusFilter === 'overdue' ? 'primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => handleStatusFilterChange('overdue')}
                >
                  Overdue
                </Button>
              </div>
            </Col>
            <Col md={6}>
              <InputGroup>
                <Form.Control
                  placeholder="Search by assignment title..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button variant="outline-primary" onClick={handleSearch}>
                  <FaSearch />
                </Button>
                {searchTerm && (
                  <Button variant="outline-secondary" onClick={handleClearSearch}>
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Col>
          </Row>

          {/* Assignments List */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading your assignments...</p>
            </div>
          ) : assignments.length === 0 ? (
            <Alert variant="info" className="text-center py-5">
              <FaClipboardList size={40} className="mb-3 text-muted" />
              <h5>No assignments found</h5>
              <p>You don't have any assignments at the moment.</p>
            </Alert>
          ) : (
            <>
              <Row className="g-4">
                {assignments.map((assignment) => (
                  <Col md={6} lg={4} key={assignment.id}>
                    <Card className="h-100 shadow-sm assignment-card">
                      <Card.Body>
                        <div className="assignment-status-badge">
                          {getStatusBadge(assignment.student_status, assignment.due_date)}
                        </div>
                        <h5 className="card-title mt-3">{assignment.title}</h5>
                        <p className="card-text text-muted small">
                          {assignment.batch_name}
                        </p>
                        <div className="assignment-meta mb-3">
                          <div className="meta-item">
                            <FaClock className="me-2 text-muted" />
                            <span className={isOverdue(assignment.due_date) && assignment.student_status === 'pending' ? 'text-danger' : ''}>
                              Due: {formatDate(assignment.due_date)}
                            </span>
                          </div>
                          <div className="meta-item">
                            <strong>Marks:</strong> {assignment.total_marks}
                          </div>
                          {assignment.obtained_marks !== null && (
                            <div className="meta-item">
                              <strong>Obtained:</strong>{' '}
                              <span className="text-success fw-bold">
                                {assignment.obtained_marks}/{assignment.total_marks}
                              </span>
                            </div>
                          )}
                        </div>
                        {assignment.feedback && (
                          <div className="assignment-feedback mb-3">
                            <small className="text-muted">
                              <strong>Feedback:</strong> {assignment.feedback}
                            </small>
                          </div>
                        )}
                      </Card.Body>
                      <Card.Footer className="bg-transparent border-0 pb-3">
                        <Button
                          variant="primary"
                          className="w-100"
                          onClick={() => handleViewAssignment(assignment.id)}
                        >
                          <FaEye className="me-2" />
                          {assignment.student_status === 'pending' ? 'Submit Assignment' : 'View Details'}
                        </Button>
                      </Card.Footer>
                    </Card>
                  </Col>
                ))}
              </Row>
              {renderPagination()}
            </>
          )}
        </Container>
      </div>
    </div>
  );
};

export default StudentAssignmentsPage;